import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { Icon } from '../components/icons';
import { Button, Field } from '../components/ui';
import { UnsupportedWidth } from '../components/shell';

export function UnlockPage(): React.JSX.Element {
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const exchange = useMutation({
    mutationFn: () => api<unknown>('/auth/exchange', { method: 'POST', body: { token } }),
    onSuccess: async () => {
      setToken('');
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      void navigate('/');
    },
  });
  const form = (
    <form
      className="unlock-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (token.trim()) exchange.mutate();
      }}
    >
      <Field
        label="Owner access token"
        hint="The token is exchanged for a secure 30-day session and is never stored in this browser."
      >
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={token}
          onChange={(event) => setToken(event.target.value)}
          placeholder="Paste your owner token"
          aria-invalid={exchange.isError}
        />
      </Field>
      {exchange.isError ? (
        <p className="form-error" role="alert">
          {exchange.error.message}
        </p>
      ) : null}
      <Button type="submit" variant="primary" disabled={!token.trim() || exchange.isPending}>
        {exchange.isPending ? 'Unlocking…' : 'Unlock Akshay’s Atlas'}
        <Icon name="arrow" size={16} />
      </Button>
    </form>
  );
  return (
    <>
      <div className="unlock-page">
        <div className="unlock-panel">
          <div className="unlock-brand">
            <span className="brand__mark brand__mark--large">
              <Icon name="atlas" size={28} />
            </span>
            <strong>Akshay’s Atlas</strong>
          </div>
          <div className="unlock-copy">
            <p className="eyebrow">Private life operations system</p>
            <h1>
              Your critical paths,
              <br />
              held in one place.
            </h1>
            <p>
              Deadlines, evidence, responsibilities and decisions—source-aware and designed for one
              owner.
            </p>
          </div>
          <div className="unlock-guardrails">
            <span>
              <Icon name="lock" />
              Token-protected
            </span>
            <span>
              <Icon name="shield" />
              Sensitive values encrypted
            </span>
            <span>
              <Icon name="research" />
              Source-backed research
            </span>
          </div>
          <p className="unlock-version">Akshay’s Atlas · V1 · Europe/Berlin</p>
        </div>
        <div className="unlock-access">
          <div className="unlock-access__inner">
            <div className="unlock-access__heading">
              <span className="unlock-icon">
                <Icon name="lock" />
              </span>
              <p className="eyebrow">Owner access</p>
              <h2>Unlock your workspace</h2>
              <p>
                A secure session cookie will keep this device signed in. Your token never appears in
                URLs or browser storage.
              </p>
            </div>
            {form}
            <div className="unlock-security-note">
              <Icon name="shield" />
              <p>
                <strong>Private by design</strong>
                <br />
                No analytics, advertising, document uploads or third-party fonts.
              </p>
            </div>
          </div>
        </div>
      </div>
      <UnsupportedWidth>{form}</UnsupportedWidth>
    </>
  );
}
